import { useEffect, useState } from "react";
import { Search, SlidersHorizontal, Briefcase, MapPin, Building2, Globe, Mail, Phone } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { ContactButton } from "@/components/ContactButton";
import api from "@/lib/api";

const JobCard = ({ j }) => {
  useEffect(() => { api.post("/track/view", { context_type: "job", context_id: j.id }).catch(() => {}); }, [j.id]);
  return (
  <div data-testid={`job-card-${j.id}`} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-5 hover:shadow-md transition-shadow flex flex-col">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-head font-semibold text-[#1c1917]">{j.title}</h3>
        <p className="text-sm text-[#1b5e3f] font-medium flex items-center gap-1 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {j.company_name}</p>
      </div>
      {j.salary && <span className="shrink-0 text-xs font-bold text-[#1b5e3f] bg-[#e8f0ec] px-3 py-1 rounded-full">{j.salary}</span>}
    </div>
    <p className="text-sm text-[#78716c] flex items-center gap-1 mt-2"><MapPin className="w-3.5 h-3.5" /> {j.city}, {j.region} · {j.sector}</p>
    {j.description && <p className="text-sm text-[#57534e] mt-2 line-clamp-3">{j.description}</p>}
    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-[#e7e5e4] text-xs">
      {j.website && <a href={j.website.startsWith("http") ? j.website : `https://${j.website}`} target="_blank" rel="noreferrer" className="text-[#1b5e3f] hover:underline inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Site</a>}
      {j.contact_email && <a href={`mailto:${j.contact_email}`} className="text-[#1b5e3f] hover:underline inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {j.contact_email}</a>}
      {j.phone && <a href={`tel:${j.phone}`} className="text-[#1b5e3f] hover:underline inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {j.phone}</a>}
    </div>
    <div className="mt-3">
      <ContactButton contextType="job" contextId={j.id} label="Conversar sobre a vaga" />
    </div>
  </div>
  );
};

export default function JobOffers() {
  const [meta, setMeta] = useState({ sectors: [], cities: [], regions: [] });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: "", city: "", region: "", q: "" });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/job-offers", { params }).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const Select = ({ field, label, options }) => (
    <div>
      <label className="text-sm font-medium text-[#1c1917]">{label}</label>
      <select
        data-testid={`job-filter-${field}`}
        value={filters[field]}
        onChange={(e) => setFilters({ ...filters, [field]: e.target.value })}
        className="w-full mt-1.5 bg-white border border-[#e7e5e4] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f]"
      >
        <option value="">Todos</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#e8f0ec] flex items-center justify-center"><Briefcase className="w-5 h-5 text-[#1b5e3f]" /></span>
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-semibold text-[#1c1917]">Ofertas de Trabalho por região</h1>
            <p className="text-[#78716c] text-sm">Encontre vagas de emprego oferecidas por empresas, por área, cidade e região.</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 mt-8">
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm p-6 lg:sticky lg:top-24 space-y-5">
              <div className="flex items-center gap-2 text-[#1c1917] font-head font-semibold"><SlidersHorizontal className="w-4 h-4" /> Filtros</div>
              <div>
                <label className="text-sm font-medium text-[#1c1917]">Buscar</label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
                  <input data-testid="job-filter-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Cargo ou empresa" className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f]" />
                </div>
              </div>
              <Select field="sector" label="Setor / Área" options={meta.sectors} />
              <Select field="city" label="Cidade" options={meta.cities} />
              <Select field="region" label="Região" options={meta.regions} />
              <button data-testid="job-filter-clear" onClick={() => setFilters({ sector: "", city: "", region: "", q: "" })} className="text-sm text-[#78716c] hover:text-[#1b5e3f] underline">Limpar filtros</button>
            </div>
          </aside>

          <main className="lg:col-span-3">
            {loading ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => <div key={i} className="h-48 rounded-2xl bg-[#e7e5e4]/40 animate-pulse" />)}
              </div>
            ) : jobs.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#e7e5e4] p-12 text-center">
                <p className="text-[#78716c]">Nenhuma oferta de trabalho encontrada com esses filtros.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#78716c] mb-4">{jobs.length} vaga(s) encontrada(s)</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  {jobs.map((j) => <JobCard key={j.id} j={j} />)}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
